// src/render/audio/footsteps.ts — Phase 6 footstep audio (06-05).
//
// CONTEXT D-09..D-12, AUDIO-02, AUDIO-08:
//   - One shared footsteps audio sprite atlas (Howler `pool`=4 — D-20).
//   - Per-character `move.quietness` modulates Howl volume (AUDIO-02):
//       volume = P6_FOOTSTEP_BASE_VOLUME
//              * (1 - quietness * P6_QUIETNESS_VOLUME_WEIGHT)
//              * distAtten
//              * sprintMult
//   - Distance-attenuation cull (AUDIO-08): skip playback if the source is
//     further than P6_FOOTSTEP_DISTANCE_CULL_TILES tiles from the local
//     player. Linear attenuation 1→0 across the cull radius.
//   - Throttle (D-12): per-entity at P6_FOOTSTEP_THROTTLE_MS (~250 ms).
//   - Sprint gain (D-12): when the entity-moved payload's sprint flag is
//     true, multiply the computed volume by P6_FOOTSTEP_SPRINT_GAIN.
//   - D-11 silent hiding: status === 'hiding' short-circuits BEFORE any
//     throttle/distance work — hidden hiders make zero footstep sound.
//
// Snapshot caching:
//   - The `'sim-tick'` payload is the source of truth for `playerPosition`
//     and `mapTileWidth` (extended by 06-05 — see src/render/bus.ts). We
//     cache the latest snapshot in `simSnapshot` and read it on every
//     `entity-moved` event.
//   - Throttle Map persists across calls (Pitfall 11: never re-allocate);
//     `'round-started'` clears it on round transitions.
//
// BLOCKER-1: the constructed footsteps atlas Howl is registered with
// audioManager.trackHowl so liveSources reflects reality (Plan 03 contract,
// AUDIO-07 ≤ 8 ceiling proof in Plan 08). Cycle-safe at runtime — see the
// matching note in waffle.ts.
//
// Source: 06-CONTEXT.md D-09..D-12, D-20; 06-PATTERNS.md
//         §src/render/audio/footsteps.ts; 06-RESEARCH.md §Footsteps + quietness.

import { Howl } from 'howler';

import type { AudioCodec, AudioManifest } from './codec';
import { srcListFor } from './codec';
import type { RenderEventMap } from '../bus';
import { renderBus } from '../bus';
import { getQuietness } from '../../sim/content/cache';
import { audioManager } from './manager';
import {
  P6_FOOTSTEP_THROTTLE_MS,
  P6_FOOTSTEP_DISTANCE_CULL_TILES,
  P6_FOOTSTEP_SPRINT_GAIN,
  P6_QUIETNESS_VOLUME_WEIGHT,
  P6_FOOTSTEP_BASE_VOLUME,
  P6_HOWL_POOL_FOOTSTEP,
} from './constants';

// ---------------------------------------------------------------------------
// Module-level state
// ---------------------------------------------------------------------------

let footstepHowl: Howl | null = null;

interface SimSnapshot {
  tick: number;
  playerPosition: { x: number; y: number } | null;
  tileWidth: number;
}
let simSnapshot: SimSnapshot | null = null;

/**
 * Per-entity throttle Map. Keyed by entityId; value = sim tick of last
 * footstep. Pitfall 11: persists across calls — `'round-started'` clears
 * (NOT re-allocates) it.
 */
const lastFootstepTickByEntity: Map<string, number> = new Map();

/** Cleanup callbacks for renderBus subscriptions, unsubscribed in destroy. */
const subscriptions: Array<() => void> = [];

/**
 * Volume floor: under this, skip the play call entirely. Two reasons:
 *   1. iOS Web Audio caps concurrent sources at ~8; spending one on an
 *      effectively-inaudible footstep is wasteful.
 *   2. Some Howler builds short-circuit volume <= 0 to a no-op anyway, so
 *      the floor matches the audible threshold a human can perceive.
 */
const VOLUME_FLOOR = 0.02;

/** D-12: sim runs at 30 Hz; throttle in ms → ticks. */
const SIM_HZ = 30;
const THROTTLE_TICKS = Math.ceil((P6_FOOTSTEP_THROTTLE_MS / 1000) * SIM_HZ);

const STEP_SPRITE_NAMES = ['step-1', 'step-2', 'step-3', 'step-4'] as const;

// ---------------------------------------------------------------------------
// Public API (matches 06-03 manager.ts seam)
// ---------------------------------------------------------------------------

export function initFootsteps(_codec: AudioCodec, manifest: AudioManifest): void {
  // Singleton: a second init() leaves the existing atlas in place.
  if (footstepHowl) return;

  footstepHowl = new Howl({
    src: srcListFor('footsteps/atlas', manifest),
    sprite: {
      'step-1': [0, 200],
      'step-2': [220, 200],
      'step-3': [440, 200],
      'step-4': [660, 200],
    },
    pool: P6_HOWL_POOL_FOOTSTEP,
    preload: false, // Pitfall 8 — load only after iOS unlock.
    volume: 1.0,
  });
  footstepHowl.load();
  // BLOCKER-1: register the atlas Howl with the manager liveSources Set.
  // UI-06: footsteps are SFX at design volume P6_FOOTSTEP_BASE_VOLUME. (Per-step
  // playback sets an explicit per-id volume from the AUDIO-02 formula, so the
  // group gain primarily documents the bus; the master still multiplies it.)
  audioManager.trackHowl(footstepHowl, 'sfx', P6_FOOTSTEP_BASE_VOLUME);

  // Cache sim-tick snapshots (player position + tile width — added in 06-05
  // via bus.ts extension) so onEntityMoved can run distance-cull math.
  subscriptions.push(
    renderBus.on('sim-tick', (e) => {
      simSnapshot = {
        tick: e.tick,
        playerPosition: e.playerPosition,
        tileWidth: e.mapTileWidth,
      };
    }),
  );

  // Pitfall 11: clear (NOT re-allocate) the per-entity throttle Map on round
  // transitions so footsteps don't carry artificial silence into a fresh round.
  subscriptions.push(
    renderBus.on('round-started', () => {
      lastFootstepTickByEntity.clear();
    }),
  );
}

/**
 * Stable seam called by manager.ts from the renderBus 'entity-moved'
 * subscriber. Order-of-operations is load-bearing:
 *   1. D-11 silent-hide gate
 *   2. throttle check
 *   3. distance cull (AUDIO-08)
 *   4. compute volume formula (AUDIO-02)
 *   5. floor check
 *   6. play sprite + set volume
 *   7. update throttle map
 */
export function onEntityMoved(e: RenderEventMap['entity-moved']): void {
  if (!footstepHowl) return;

  // D-11: hidden hiders are silent. Short-circuit BEFORE any compute.
  if (e.status === 'hiding') return;

  // Throttle (D-12). Per-entity tick delta against the throttle window.
  const lastTick = lastFootstepTickByEntity.get(e.entityId) ?? -Infinity;
  if (e.tick - lastTick < THROTTLE_TICKS) return;

  // Distance cull (AUDIO-08). Player position + tile width come from the
  // last cached sim-tick snapshot; both must be present for cull math.
  const snap = simSnapshot;
  if (!snap || !snap.playerPosition) return;
  const playerPos = snap.playerPosition;
  const distPx = Math.hypot(
    playerPos.x - e.position.x,
    playerPos.y - e.position.y,
  );
  const cullPx = P6_FOOTSTEP_DISTANCE_CULL_TILES * snap.tileWidth;
  if (distPx > cullPx) return;

  // Linear distance attenuation 1→0 across the cull radius.
  const distAtten = Math.max(0, 1 - distPx / cullPx);

  // AUDIO-02 volume formula.
  const quietness = getQuietness(e.characterId).move;
  const sprintMult = e.sprint ? P6_FOOTSTEP_SPRINT_GAIN : 1.0;
  const volume = P6_FOOTSTEP_BASE_VOLUME * (1 - quietness * P6_QUIETNESS_VOLUME_WEIGHT) * distAtten * sprintMult;

  if (volume < VOLUME_FLOOR) return;

  // Pick a step sprite alternately. Use the entity tick as a cheap rotation
  // source — deterministic across same-tick multi-entity emits and avoids
  // re-using rngFn for a render-only audio detail.
  const sprite = STEP_SPRITE_NAMES[e.tick % STEP_SPRITE_NAMES.length]!;
  const id = footstepHowl.play(sprite);
  footstepHowl.volume(volume, id);

  lastFootstepTickByEntity.set(e.entityId, e.tick);
}

export function destroyFootsteps(): void {
  for (const off of subscriptions) off();
  subscriptions.length = 0;
  lastFootstepTickByEntity.clear();
  simSnapshot = null;
  footstepHowl?.unload();
  footstepHowl = null;
}
